import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { AppUrls } from '../app-urls';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { CaptchaSettings, ILocalStorageCaptchaSettings, previousActions } from '../models/сaptcha';
import { catchError, filter, first, tap } from 'rxjs/operators';
import { LocalStorageService } from './local-storage.service';

@Injectable({
  providedIn: 'root',
})
export class CaptchaService {
  private readonly rootUrl: string = environment.urls.rootUrl;
  private _captchaToken$: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  private _captchaHeader$: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  private _isCaptchaPassed: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private _isCaptchaVisible$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private _previousActionKey$: BehaviorSubject<previousActions> = new BehaviorSubject<previousActions>(null);
  private _settings$: BehaviorSubject<CaptchaSettings> = new BehaviorSubject<CaptchaSettings>(null);

  constructor(
    private http: HttpClient,
    private _localStorageService: LocalStorageService<ILocalStorageCaptchaSettings>,
  ) {}

  public getCaptchaSettings() {
    const url = `${this.rootUrl}${AppUrls.captcha.settings}`;
    return this.http.get<CaptchaSettings>(url);
  }

  private _disableCaptcha(onErrorCallback?: () => void) {
    this.isCaptchaVisible = false;
    this.isCaptchaPassed  = true;
    if (onErrorCallback) {
      onErrorCallback();
    }
  }

  public initCaptchaSettings(previousActionKey?: previousActions, onErrorCallback?: () => void): void {
    if (previousActionKey) {
      this._previousActionKey$.next(previousActionKey);
      this._localStorageService.setItem('PreviousActionKey', { previousActionKey: previousActionKey });
    }
    this.isCaptchaPassed = false;


    this.getCaptchaSettings()
      .pipe(
        first((value) => !!value),
        tap((settings: CaptchaSettings) => {
          if (settings) {
            if (settings?.IsEnabled === false) {
              this._disableCaptcha(onErrorCallback);
            } else {
              this.settings = settings;
              this.captchaHeader$ = settings.TokenHeaderName || '';
              this.isCaptchaVisible = true;
            }
          }
        }),
        catchError((err: Error) => {
          this._disableCaptcha(onErrorCallback);
          return of(err);
        }),
      )
      .subscribe();
  }

  public get isCaptchaVisible$(): Observable<boolean> {
    return this._isCaptchaVisible$.asObservable();
  }

  public get isCaptchaVisible(): boolean {
    return this._isCaptchaVisible$.getValue();
  }

  public set isCaptchaVisible(value: boolean) {
    this._isCaptchaVisible$.next(value);
  }

  get settings$(): BehaviorSubject<CaptchaSettings> {
    return this._settings$;
  }

  public set settings(settings: CaptchaSettings) {
    this._settings$.next(settings);
  }

  public get captchaToken$(): string {
    return this._captchaToken$.getValue();
  }

  public set captchaToken$(value: string) {
    this._captchaToken$.next(value);
  }

  get isCaptchaPassed$(): Observable<boolean> {
    return this._isCaptchaPassed.asObservable();
  }

  set isCaptchaPassed(value: boolean) {
    this._isCaptchaPassed.next(value);
  }

  public get captchaHeader$(): string {
    return this._captchaHeader$.getValue();
  }

  public set captchaHeader$(value: string) {
    this._captchaHeader$.next(value);
  }

  public get previousActionKey$(): previousActions {
    return this._previousActionKey$.getValue();
  }

  public set previousActionKey(value: previousActions) {
    this._previousActionKey$.next(value);
  }
}
